// SCR-STAFF-938 — داشبورد تجهیزات (US-ORG-053، AC-ORG-053.1؛ US-ORG-043): نگهداری Due و Overdue و رزروشده، گواهی‌های رو به انقضا، دستورکارهای باز به
// تفکیک شدت، مغایرت‌های شمارنده، دستگاه‌های خارج از سرویس و قراردادهای رو به پایان؛ و جدول بهره‌وری همه دستگاه‌های شعبه با هزینه به ازای واحد
// (C-AGENT-1274، 1278). هر بخش به شناسنامه دستگاه پیوند دارد.
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type DeviceUtilizationView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, EmptyState, ErrorState, faDigits, JalaliDate, ListPage, LoadingState, Money, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { BranchFilter, useBranchSelection } from './common';
import { percentOf } from './deviceCommon';
import { lastDays, maintenanceKeys, occurrenceTone, validityTone } from './DeviceMaintenanceTabs';

export const SCR_STAFF_938 = 'SCR-STAFF-938';

function DeviceLink({ id, children }: { id: string; children: ReactNode }) {
  return (
    <Link component={RouterLink} to={`/settings/devices/${id}`}>
      {children}
    </Link>
  );
}

function Section({ id, titleKey, count, children }: { id: string; titleKey: string; count: number; children: ReactNode }) {
  const { t } = useTranslation('org');
  return (
    <Grid size={{ xs: 12, md: 6 }}>
      <Card variant="outlined" sx={{ height: '100%' }} data-testid={`equipment-${id}`}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" component="h2">
              {t(titleKey)}
            </Typography>
            <Chip size="small" label={faDigits(count)} color={count > 0 ? 'warning' : 'default'} data-testid={`equipment-${id}-count`} />
          </Stack>
          {count === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('org:equipment.none')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {children}
            </List>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
}

export function EquipmentDashboardPage() {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const branches = useBranchSelection();
  const [days, setDays] = useState(30);
  const dashboard = useQuery({
    queryKey: [...maintenanceKeys.dashboard(session?.tenantId), branches.branchId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/equipment-dashboard', { params: { query: { branchId: branches.branchId! } } })),
    enabled: Boolean(branches.branchId),
    refetchInterval: 60_000,
  });
  const range = lastDays(days);
  const utilization = useQuery({
    queryKey: [...maintenanceKeys.dashboard(session?.tenantId), branches.branchId, 'utilization', days],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/device-utilization', { params: { query: { branchId: branches.branchId!, from: range.from, to: range.to } } })),
    enabled: Boolean(branches.branchId),
  });
  const data = dashboard.data;
  const hours = (minutes: number | string) => faDigits((Number(minutes) / 60).toFixed(1).replace(/\.0$/, ''));
  const columns: DataTableColumn<DeviceUtilizationView>[] = [
    { id: 'device', headerKey: 'org:equipment.columns.device', cell: (row) => <DeviceLink id={row.deviceId}>{row.name}</DeviceLink> },
    { id: 'booked', headerKey: 'org:equipment.columns.booked', cell: (row) => t('org:utilization.hours', { n: hours(row.bookedMinutes) }) },
    { id: 'downtime', headerKey: 'org:equipment.columns.downtime', cell: (row) => t('org:utilization.hours', { n: hours(row.downtimeMinutes) }) },
    { id: 'rate', headerKey: 'org:equipment.columns.rate', cell: (row) => t('org:counter.percent', { percent: percentOf(row.utilizationBasisPoints) }) },
    { id: 'usage', headerKey: 'org:equipment.columns.usage', cell: (row) => `${faDigits(Number(row.counterUsage))} ${t(`org:counter_unit.${row.counterUnit}`)}` },
    { id: 'usage-cost', headerKey: 'org:equipment.columns.usage_cost', cell: (row) => <Money amountMinor={Number(row.usageCost.amount)} /> },
    { id: 'maintenance-cost', headerKey: 'org:equipment.columns.maintenance_cost', cell: (row) => <Money amountMinor={Number(row.maintenanceCost.amount)} /> },
    { id: 'revenue', headerKey: 'org:equipment.columns.revenue', cell: (row) => <Money amountMinor={Number(row.revenue.amount)} /> },
    {
      id: 'per-unit',
      headerKey: 'org:equipment.columns.per_unit',
      cell: (row) => (
        <span data-testid={`equipment-per-unit-${row.code}`}>
          {row.currentRate ? <Money amountMinor={Number(row.currentRate.rate.amount)} /> : '—'}
          {row.maintenanceCostPerUnit ? (
            <>
              {' + '}
              <Money amountMinor={Number(row.maintenanceCostPerUnit.amount)} />
            </>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_938}
      titleKey="org:equipment.title"
      subtitleKey="org:equipment.subtitle"
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <BranchFilter options={branches.options} value={branches.branchId} onChange={branches.setBranchId} />
          <TextField select size="small" label={t('org:utilization.period')} value={days} onChange={(event) => setDays(Number(event.target.value))} sx={{ minWidth: 180 }} slotProps={{ select: { native: true }, htmlInput: { 'data-testid': 'equipment-period' } }}>
            {[7, 30, 90, 365].map((value) => (
              <option key={value} value={value}>
                {t('org:utilization.last_days', { n: faDigits(value) })}
              </option>
            ))}
          </TextField>
        </Stack>
      }
    >
      {branches.options.length === 0 && <EmptyState titleKey="org:no_branch" />}
      {dashboard.isPending && branches.branchId && <LoadingState rows={3} />}
      {dashboard.error ? <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} /> : null}
      {data && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Section id="maintenance" titleKey="org:equipment.maintenance" count={data.overdueMaintenance.length + data.dueMaintenance.length + data.bookedMaintenance.length}>
            {[...data.overdueMaintenance, ...data.dueMaintenance, ...data.bookedMaintenance].map((item) => (
              <ListItem key={item.id} disableGutters secondaryAction={<StatusChip tone={occurrenceTone(item.status)} labelKey={`org:occurrence_status.${item.status}`} />}>
                <ListItemText
                  primary={<DeviceLink id={item.deviceId}>{`${item.deviceName ?? ''} — ${item.planName}`}</DeviceLink>}
                  secondary={item.dueOn ? <JalaliDate value={item.dueOn} /> : t('org:maintenance.due_counter', { due: faDigits(Number(item.dueAtCounter ?? 0)), warn: faDigits(Number(item.warnAtCounter ?? 0)) })}
                />
              </ListItem>
            ))}
          </Section>
          <Section id="certificates" titleKey="org:equipment.certificates" count={data.expiringCertificates.length}>
            {data.expiringCertificates.map((item) => (
              <ListItem key={item.certificateId} disableGutters secondaryAction={<StatusChip tone={validityTone(item.validity)} labelKey={`org:certificate_validity.${item.validity}`} />}>
                <ListItemText primary={<DeviceLink id={item.deviceId}>{`${item.deviceName} — ${t(`org:certificate_type.${item.type}`)}`}</DeviceLink>} secondary={<JalaliDate value={item.expiresOn} />} />
              </ListItem>
            ))}
          </Section>
          <Section id="work-orders" titleKey="org:equipment.work_orders" count={data.openWorkOrders.length}>
            <ListItem disableGutters>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {data.openWorkOrdersBySeverity.map((item) => (
                  <Chip key={item.severity} size="small" label={`${t(`org:severity.${item.severity}`)}: ${faDigits(Number(item.count))}`} />
                ))}
              </Stack>
            </ListItem>
            {data.openWorkOrders.slice(0, 10).map((item) => (
              <ListItem key={item.id} disableGutters>
                <ListItemText
                  primary={
                    <Link component={RouterLink} to={`/work-orders?id=${item.id}`}>
                      <bdi dir="ltr">{item.number}</bdi> — {item.resourceName ?? ''}
                    </Link>
                  }
                  secondary={t(`org:work_order_status.${item.status}`)}
                />
              </ListItem>
            ))}
          </Section>
          <Section id="counter-alerts" titleKey="org:equipment.counter_alerts" count={data.counterAlerts.length}>
            {data.counterAlerts.map((item) => (
              <ListItem key={item.alertId} disableGutters>
                <ListItemText primary={<DeviceLink id={item.deviceId}>{item.deviceName}</DeviceLink>} secondary={t(`org:counter.alert_short.${item.kind}`, { percent: percentOf(item.discrepancyBasisPoints) })} />
              </ListItem>
            ))}
          </Section>
          <Section id="out-of-service" titleKey="org:equipment.out_of_service" count={data.outOfServiceDevices.length}>
            {data.outOfServiceDevices.map((item) => (
              <ListItem key={item.deviceId} disableGutters>
                <ListItemText primary={<DeviceLink id={item.deviceId}>{item.name}</DeviceLink>} secondary={item.reason ?? t(`org:device_status.${item.status}`)} />
              </ListItem>
            ))}
          </Section>
          <Section id="contracts" titleKey="org:equipment.contracts" count={data.expiringContracts.length}>
            {data.expiringContracts.map((item) => (
              <ListItem key={item.contractId} disableGutters>
                <ListItemText primary={<DeviceLink id={item.deviceId}>{`${item.deviceName} — ${t(`org:contract_kind.${item.kind}`)} (${item.partyName})`}</DeviceLink>} secondary={<JalaliDate value={item.endsOn} />} />
              </ListItem>
            ))}
          </Section>
        </Grid>
      )}
      {branches.branchId && (
        <DataTable
          captionKey="org:equipment.utilization_caption"
          columns={columns}
          rows={utilization.data?.items}
          getRowId={(row) => row.deviceId}
          isLoading={utilization.isPending}
          error={utilization.error}
          onRetry={() => void utilization.refetch()}
          emptyTitleKey="org:equipment.utilization_empty"
        />
      )}
    </ListPage>
  );
}
